import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { departmentsApi } from '../../api/facades';

const inputClass = 'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const DepartmentCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [department_name, setDepartmentName] = useState('');
  const [people_id, setPeopleId] = useState<string>('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [description, setDescription] = useState('');

  const create = useMutation({
    mutationFn: () =>
      departmentsApi.create({
        department_name,
        people_id: people_id === '' ? null : parseInt(people_id, 10),
        email: email || null,
        mobile: mobile || null,
        description: description || null,
      }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      const id = data?.data?.id ?? (data?.data && (data.data as any).id);
      if (id) navigate(`/dashboard/settings/departments/${id}`);
      else navigate('/dashboard/settings/departments');
      toast.success('Department created.');
    },
    onError: (e: any) => toast.error(e?.message || 'Create failed.'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!department_name.trim()) return;
    create.mutate();
  };

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <h1 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-4">New Department</h1>
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <FormField label="Name">
            <input className={inputClass} value={department_name} onChange={(e) => setDepartmentName(e.target.value)} placeholder="e.g. Litigation" required />
          </FormField>
        </div>
        <FormField label="People ID (optional)" hint="ID of department lead">
          <input className={inputClass} type="number" value={people_id} onChange={(e) => setPeopleId(e.target.value)} placeholder="—" />
        </FormField>
        <FormField label="Email (optional)">
          <input className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="dept@example.com" />
        </FormField>
        <FormField label="Mobile (optional)">
          <input className={inputClass} value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+1…" />
        </FormField>
        <div className="md:col-span-2">
          <FormField label="Description (optional)">
            <textarea className={inputClass} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="—" />
          </FormField>
        </div>
        <div className="md:col-span-2 flex gap-2 mt-2">
          <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!department_name.trim() || create.isPending}>{create.isPending ? 'Creating…' : 'Create'}</Button>
        </div>
      </form>
    </div>
  );
};

export default DepartmentCreatePage;
